import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { FloppyDisk, Image as ImageIcon, X } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import AccountingEmailSettings from '../components/AccountingEmailSettings';
import DangerZone from '../components/DangerZone';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    name: '',
    address: '',
    postal_code: '',
    city: '',
    country: 'France',
    phone: '',
    email: '',
    website: '',
    siret: '',
    tva_number: '',
    logo: '',
    default_payment_terms: '',
    default_footer: '',
    bank_details: '',
    vat_rate: 0.085,
    invoice_prefix: 'F',
    quote_prefix: 'D',
    credit_note_prefix: 'A',
    numbering_format: '{prefix}{year}-{seq:04d}',
    invoice_yearly_counter: true,
    timezone: 'Indian/Reunion',
    accounting_recipient_emails: [],
    accounting_export_files: ['sales', 'purchases', 'cash', 'bank', 'ledger', 'fec'],
    accounting_export_format: 'xlsx',
    accounting_auto_send: false,
    accounting_auto_send_day: 1,
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/company-settings`, {
        withCredentials: true,
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 2 Mo');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setSettings({ ...settings, logo: event.target.result });
      toast.success('Logo chargé. N\'oubliez pas de sauvegarder.');
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setSettings({ ...settings, logo: '' });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/company-settings`, settings, {
        withCredentials: true,
      });
      toast.success('Paramètres enregistrés');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="App">
        <Sidebar />
        <div className="main-content">
          <div className="text-center py-12">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="settings-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Paramètres de la société</h1>
            <p className="text-slate-600">Personnalisez les informations apparaissant sur vos documents</p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-settings-btn"
            className="bg-orange-500 hover:bg-orange-400 text-white"
          >
            <FloppyDisk size={20} weight="bold" className="mr-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>

        <Tabs defaultValue="company" className="bg-white border border-slate-200 rounded-lg p-6">
          <TabsList className="grid w-full grid-cols-5 mb-6">
            <TabsTrigger value="company" data-testid="tab-company">Informations</TabsTrigger>
            <TabsTrigger value="logo" data-testid="tab-logo">Logo</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
            <TabsTrigger value="accounting" data-testid="tab-accounting">Comptabilité</TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="tab-maintenance">Maintenance</TabsTrigger>
          </TabsList>

          <TabsContent value="company" className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Identité de la société</h2>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom de la société *</label>
              <Input
                value={settings.name}
                onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                data-testid="settings-name-input"
                placeholder="Ma Société SARL"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Adresse</label>
              <Input
                value={settings.address}
                onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                data-testid="settings-address-input"
                placeholder="12 rue de la République"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Code postal</label>
                <Input
                  value={settings.postal_code}
                  onChange={(e) => setSettings({ ...settings, postal_code: e.target.value })}
                  data-testid="settings-postal-code-input"
                  placeholder="75001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
                <Input
                  value={settings.city}
                  onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                  data-testid="settings-city-input"
                  placeholder="Paris"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Pays</label>
              <Input
                value={settings.country}
                onChange={(e) => setSettings({ ...settings, country: e.target.value })}
                data-testid="settings-country-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
                <Input
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  data-testid="settings-phone-input"
                  placeholder="01 23 45 67 89"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <Input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  data-testid="settings-email-input"
                  placeholder="contact@masociete.fr"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Site web</label>
              <Input
                value={settings.website}
                onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                data-testid="settings-website-input"
                placeholder="www.masociete.fr"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SIRET</label>
                <Input
                  value={settings.siret}
                  onChange={(e) => setSettings({ ...settings, siret: e.target.value })}
                  data-testid="settings-siret-input"
                  placeholder="123 456 789 00010"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">N° TVA</label>
                <Input
                  value={settings.tva_number}
                  onChange={(e) => setSettings({ ...settings, tva_number: e.target.value })}
                  data-testid="settings-tva-input"
                  placeholder="FR12345678901"
                />
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <label className="block text-sm font-medium text-blue-900 mb-2">
                Taux de TVA appliqué sur les factures, devis et avoirs
              </label>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  max="1"
                  value={settings.vat_rate}
                  onChange={(e) => setSettings({ ...settings, vat_rate: parseFloat(e.target.value) || 0 })}
                  data-testid="settings-vat-rate-input"
                  className="w-32"
                />
                <span className="text-sm text-blue-900">
                  soit <strong>{((settings.vat_rate || 0) * 100).toFixed(1)}%</strong>
                </span>
              </div>
              <p className="text-xs text-blue-700 mt-2">
                Les prix saisis dans les produits/prestations sont considérés comme TTC. L'application calcule automatiquement le HT et la TVA sur les documents.
                Exemple : 0.085 = 8.5% (taux Réunion/DOM), 0.20 = 20% (taux métropole), 0 = exonéré.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-300 rounded-lg p-4 mt-4">
              <label className="block text-sm font-medium text-slate-900 mb-2">
                Fuseau horaire
              </label>
              <select
                value={settings.timezone || 'Indian/Reunion'}
                onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
                data-testid="settings-timezone-select"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="Indian/Reunion">Indian/Reunion — La Réunion (UTC+4)</option>
                <option value="Indian/Mayotte">Indian/Mayotte — Mayotte (UTC+3)</option>
                <option value="America/Martinique">America/Martinique — Martinique (UTC−4)</option>
                <option value="America/Guadeloupe">America/Guadeloupe — Guadeloupe (UTC−4)</option>
                <option value="America/Cayenne">America/Cayenne — Guyane (UTC−3)</option>
                <option value="Pacific/Tahiti">Pacific/Tahiti — Polynésie (UTC−10)</option>
                <option value="Pacific/Noumea">Pacific/Noumea — Nouvelle-Calédonie (UTC+11)</option>
                <option value="Europe/Paris">Europe/Paris — France métropolitaine (UTC+1/+2)</option>
                <option value="UTC">UTC — Temps universel</option>
              </select>
              <p className="text-xs text-slate-600 mt-2">
                Toutes les dates affichées sur les PDFs et exports seront converties dans ce fuseau horaire.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="logo" className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Logo de la société</h2>
            <p className="text-sm text-slate-600">
              Format recommandé : PNG ou JPG, dimensions 200x125 px (ratio 16:10), poids max 2 Mo
            </p>

            {settings.logo ? (
              <div className="border-2 border-blue-300 rounded-lg p-6 bg-blue-50 shadow-sm">
                <div className="flex items-start gap-4">
                  <img
                    src={settings.logo}
                    alt="Logo"
                    className="w-48 h-auto border-2 border-slate-300 bg-white p-2 shadow"
                    data-testid="logo-preview"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-slate-700 mb-3">Aperçu du logo actuel</p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="change-logo-btn"
                      >
                        <ImageIcon size={18} className="mr-2" />
                        Changer
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={removeLogo}
                        data-testid="remove-logo-btn"
                        className="text-red-600 hover:text-red-700"
                      >
                        <X size={18} className="mr-2" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-4 border-dashed border-blue-400 rounded-lg p-12 text-center cursor-pointer hover:border-blue-600 hover:bg-blue-100 bg-blue-50 transition-colors duration-200 shadow-sm"
                data-testid="upload-logo-zone"
              >
                <ImageIcon size={56} className="mx-auto text-blue-500 mb-3" />
                <p className="text-slate-900 font-semibold mb-1">Cliquez pour téléverser un logo</p>
                <p className="text-sm text-slate-600">PNG, JPG (max 2 Mo) — apparaîtra sur factures, devis et emails</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
              data-testid="logo-file-input"
            />
          </TabsContent>

          <TabsContent value="documents" className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Personnalisation des documents</h2>
            <p className="text-sm text-slate-600 mb-4">
              Ces informations apparaîtront en bas de vos devis et factures
            </p>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Conditions de paiement</label>
              <textarea
                value={settings.default_payment_terms}
                onChange={(e) => setSettings({ ...settings, default_payment_terms: e.target.value })}
                data-testid="settings-payment-terms-input"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Paiement à réception de facture. Pénalités de retard : 3 fois le taux légal."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Coordonnées bancaires</label>
              <textarea
                value={settings.bank_details}
                onChange={(e) => setSettings({ ...settings, bank_details: e.target.value })}
                data-testid="settings-bank-details-input"
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Banque : XXX&#10;IBAN : FR76 XXXX XXXX XXXX XXXX XXXX XXX&#10;BIC : XXXXXXXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Pied de page</label>
              <textarea
                value={settings.default_footer}
                onChange={(e) => setSettings({ ...settings, default_footer: e.target.value })}
                data-testid="settings-footer-input"
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Merci de votre confiance"
              />
            </div>

            <div className="border-2 border-slate-300 rounded-lg p-4 bg-slate-50 mt-6">
              <h3 className="text-base font-bold text-slate-900 mb-3">Numérotation des documents</h3>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Préfixe Facture</label>
                  <Input
                    value={settings.invoice_prefix}
                    onChange={(e) => setSettings({ ...settings, invoice_prefix: e.target.value })}
                    data-testid="invoice-prefix-input"
                    maxLength={6}
                    placeholder="F"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Préfixe Devis</label>
                  <Input
                    value={settings.quote_prefix}
                    onChange={(e) => setSettings({ ...settings, quote_prefix: e.target.value })}
                    data-testid="quote-prefix-input"
                    maxLength={6}
                    placeholder="D"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Préfixe Avoir</label>
                  <Input
                    value={settings.credit_note_prefix}
                    onChange={(e) => setSettings({ ...settings, credit_note_prefix: e.target.value })}
                    data-testid="credit-note-prefix-input"
                    maxLength={6}
                    placeholder="A"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Format de numérotation</label>
                <Input
                  value={settings.numbering_format}
                  onChange={(e) => setSettings({ ...settings, numbering_format: e.target.value })}
                  data-testid="numbering-format-input"
                  placeholder="{prefix}{year}-{seq:04d}"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Variables disponibles : <code>{'{prefix}'}</code>, <code>{'{year}'}</code>, <code>{'{seq}'}</code>, <code>{'{seq:04d}'}</code> (padding à 4 chiffres)
                </p>
                <p className="text-xs text-blue-700 mt-2">
                  Exemple actuel : <strong>{(settings.numbering_format || '{prefix}{year}-{seq:04d}')
                    .replace('{prefix}', settings.invoice_prefix || 'F')
                    .replace('{year}', new Date().getFullYear())
                    .replace(/\{seq:?\d*d?\}/, '0001')}</strong>
                </p>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <input
                  type="checkbox"
                  id="yearly_counter"
                  checked={settings.invoice_yearly_counter !== false}
                  onChange={(e) => setSettings({ ...settings, invoice_yearly_counter: e.target.checked })}
                  data-testid="yearly-counter-checkbox"
                />
                <label htmlFor="yearly_counter" className="text-sm text-slate-700">
                  Réinitialiser le compteur chaque année
                </label>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="accounting" className="space-y-4">
            <AccountingEmailSettings settings={settings} setSettings={setSettings} />
          </TabsContent>

          <TabsContent value="maintenance" className="space-y-4">
            <DangerZone />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;
